import { type FC, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { type GuidelineDto, GuidelineType } from "shared";
import { Modal, useModal } from "../common/modal/Modal";
import { Button } from "../components/Button";
import { useGuidelines, useGuidelinesActions } from "./useGuidelines";

export const ModalGuidelines: FC = () => {
	const { close } = useModal();
	const { data: guidelines = [], isLoading } = useGuidelines();
	const { create, remove, uploadDocument } = useGuidelinesActions();

	const [inclusionText, setInclusionText] = useState("");
	const [exclusionText, setExclusionText] = useState("");
	const inclusionFileRef = useRef<HTMLInputElement>(null);
	const exclusionFileRef = useRef<HTMLInputElement>(null);

	const inclusions = guidelines.filter(
		(g) => g.type === GuidelineType.INCLUSION,
	);
	const exclusions = guidelines.filter(
		(g) => g.type === GuidelineType.EXCLUSION,
	);

	const handleAddGuideline = async (type: GuidelineType) => {
		const text =
			type === GuidelineType.INCLUSION ? inclusionText : exclusionText;
		if (!text.trim()) return;

		await create.mutateAsync({ type, content: text });
		if (type === GuidelineType.INCLUSION) setInclusionText("");
		else setExclusionText("");
	};

	const handleFileUpload = async (type: GuidelineType, file: File) => {
		await uploadDocument.mutateAsync({ type, file });
	};

	const renderSection = (
		title: string,
		type: GuidelineType,
		items: GuidelineDto[],
		value: string,
		onChange: (v: string) => void,
		fileRef: React.RefObject<HTMLInputElement | null>,
	) => (
		<div className="mb-4">
			<h6 className="mb-2">{title}</h6>
			<p className="text-muted small mb-2">
				{type === GuidelineType.INCLUSION
					? "Add rules for what to include in generated posts"
					: "Add rules for what to avoid in generated posts"}
			</p>

			{items.length > 0 && (
				<ul className="list-group mb-3">
					{items.map((item) => (
						<li
							key={item.id}
							className="list-group-item d-flex justify-content-between align-items-start"
						>
							<div className="flex-grow-1">
								<div className="small text-break">{item.content}</div>
								{item.documentName && (
									<small className="text-muted">
										From: {item.documentName}
									</small>
								)}
							</div>
							<button
								type="button"
								className="btn btn-link btn-sm text-danger p-0 ms-2"
								onClick={() => remove.mutate(item.id)}
								disabled={remove.isPending}
							>
								Remove
							</button>
						</li>
					))}
				</ul>
			)}

			<div className="d-flex gap-2 mb-2">
				<Form.Control
					as="textarea"
					rows={2}
					value={value}
					onChange={(e) => onChange(e.target.value)}
					placeholder={`Enter ${type === GuidelineType.INCLUSION ? "inclusion" : "exclusion"} rule...`}
				/>
			</div>
			<div className="d-flex gap-2">
				<Button
					size="sm"
					onClick={() => handleAddGuideline(type)}
					disabled={!value.trim() || create.isPending}
					loading={create.isPending}
				>
					Add Rule
				</Button>
				<input
					type="file"
					ref={fileRef}
					className="d-none"
					accept=".txt,.pdf,.doc,.docx"
					onChange={(e) => {
						const file = e.target.files?.[0];
						if (file) handleFileUpload(type, file);
						e.target.value = "";
					}}
				/>
				<Button
					variant="ghost"
					size="sm"
					onClick={() => fileRef.current?.click()}
					disabled={uploadDocument.isPending}
					loading={uploadDocument.isPending}
				>
					Upload Document
				</Button>
			</div>
		</div>
	);

	return (
		<Modal size="lg">
			<h5 className="mb-3">Post Guidelines</h5>
			<p className="text-muted mb-4">
				Set up guidelines that will be automatically applied to all AI-generated
				social media posts.
			</p>

			{isLoading ? (
				<div className="text-center py-4">Loading...</div>
			) : (
				<>
					{renderSection(
						"Inclusions",
						GuidelineType.INCLUSION,
						inclusions,
						inclusionText,
						setInclusionText,
						inclusionFileRef,
					)}

					<hr />

					{renderSection(
						"Exclusions",
						GuidelineType.EXCLUSION,
						exclusions,
						exclusionText,
						setExclusionText,
						exclusionFileRef,
					)}
				</>
			)}

			<div className="mt-4">
				<Button variant="ghost" onClick={close} full>
					Close
				</Button>
			</div>
		</Modal>
	);
};
